import { GetStaticPathsResult, GetStaticPropsContext } from 'next';
import dynamic from 'next/dynamic';
import { partition } from 'lodash-es';

import { Box } from '@chakra-ui/react';

import { ShapesBanner } from '~/components/shapes-banner';
import { BigSpace } from '~/components/space';
import { PageCategory } from '~/features/analytics/constants';
import { trackEvent } from '~/features/analytics/track-event';
import { getCatalogItemsFromServer } from '~/features/catalog/controllers/get-catalog-items-from-server';
import { CatalogItem } from '~/features/catalog/models/catalog-item';
import { publicEnv } from '~/features/environment/public';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { DownloadLearningPathsSectionProps } from '~/features/learning-pathways/components/download-learning-paths-section';
import learningPathsProps from '~/features/learning-pathways/data.json';
import { logger } from '~/features/logging/logger';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';
import { LogoGardenSection } from '~/features/plans/components/logo-garden';
import { AcrossIndustriesSection } from '~/features/school/components/across-industries-section';
import { HeroSection } from '~/features/school/components/hero-section';
import { LearningPathsSection } from '~/features/school/components/learning-paths-section';
import { OtherSchoolsSection } from '~/features/school/components/other-schools-section';
import { ProgramsListSection } from '~/features/school/components/programs-list-section';
import { getAllSchoolContent } from '~/features/school/controllers/get-all-school-content';
import {
  getLearningPathsProgramContentMap,
  LearningPathProgramContentMap,
} from '~/features/school/controllers/get-learning-paths-program-content-map';
import { getSchoolContent } from '~/features/school/controllers/get-school-content';
import { SchoolContent } from '~/features/school/models/school-content';
import { BreadcrumbStructuredData } from '~/features/seo/components/breadcrumb-structured-data';
import { CourseListStructuredData } from '~/features/seo/components/course-list-structured-data';
import { CATALOG_BREADCRUMB, HOME_BREADCRUMB } from '~/features/seo/utils/create-breadcrumb-structured-data';

const DownloadLearningPathsSection = dynamic<DownloadLearningPathsSectionProps>(() =>
  import('~/features/learning-pathways/components/download-learning-paths-section').then(
    (res) => res.DownloadLearningPathsSection
  )
);

export default function SchoolPage({
  page,
  schoolContent,
  otherSchools,
  learningPathProgramContentMap,
  freeCatalogItems,
  topCatalogItems,
  downloadLearningPathsProps,
}: {
  page: Page;
  schoolContent: SchoolContent;
  learningPathProgramContentMap: LearningPathProgramContentMap;
  otherSchools: Array<{ title: string; href: string }>;
  freeCatalogItems: CatalogItem[];
  topCatalogItems: CatalogItem[];
  downloadLearningPathsProps?: DownloadLearningPathsSectionProps | null;
}) {
  const {
    title,
    titlePrefix,
    graphic,
    description,
    slug,
    learningPaths,
    acrossIndustries,
    linkToCatalog,
    affiliateCompanies,
    acrossIndustriesTitle,
  } = schoolContent;

  return (
    <>
      <PageHead page={page} pageCategory={PageCategory.School} schoolContent={schoolContent} />
      <BreadcrumbStructuredData
        crumbs={[HOME_BREADCRUMB, CATALOG_BREADCRUMB, { name: title!, url: `https://www.udacity.com/school/${slug}` }]}
      />
      <CourseListStructuredData
        items={[...topCatalogItems, ...freeCatalogItems].map((program) => ({
          slug: program.slug,
          title: program.title,
          description: program.programDescriptionOverride ?? program.summary,
        }))}
      />
      <PageLayout withoutVerticalPadding>
        <HeroSection
          browText={titlePrefix}
          catalogUrl={linkToCatalog!}
          description={description!}
          image={graphic}
          slug={slug}
          title={title!}
        />

        <Box borderTop='1px solid' borderTopColor='gray.200'>
          {downloadLearningPathsProps && <DownloadLearningPathsSection {...downloadLearningPathsProps} />}

          {learningPaths && learningPathProgramContentMap && (
            <LearningPathsSection
              learningPathProgramContentMap={learningPathProgramContentMap}
              learningPaths={learningPaths}
            />
          )}
        </Box>

        {!!topCatalogItems.length && (
          <ProgramsListSection
            programs={topCatalogItems}
            title={`Popular ${title} Programs`}
            viewAllLink={linkToCatalog!}
          />
        )}
        <AcrossIndustriesSection cards={acrossIndustries} title={acrossIndustriesTitle} />
        {affiliateCompanies && (
          <LogoGardenSection
            logos={affiliateCompanies}
            theme='light'
            title={`Programs Co-created With ${title} Leaders.`}
          />
        )}
        {!!freeCatalogItems.length && (
          <ProgramsListSection
            programs={freeCatalogItems}
            title={`Free ${title} Programs`}
            viewAllLink={linkToCatalog?.replace('any-price', 'free')!}
          />
        )}
        <OtherSchoolsSection schools={otherSchools} title='Explore Other Schools' />
        <BigSpace />
        <ShapesBanner
          subTitle='Get Started Today'
          title='The Latest Technical Skills. Yours For The Taking.'
          primaryCta={{
            buttonLabel: 'Start For Free',
            buttonAction: 'Route To Sign Up',
            buttonColorScheme: 'sea-foam',
            buttonVariant: 'solid',
          }}
          onPrimaryCtaClick={() =>
            trackEvent({
              name: 'Schools Page CTA Clicked',
              type: 'click',
              label: 'learn_for_free_clicked',
            })
          }
        />
      </PageLayout>
    </>
  );
}

const legacySchoolSlugs = [
  'artificial-intelligence',
  'autonomous-systems',
  'business',
  'career-resources',
  'cloud-computing',
  'cybersecurity',
  'data-science',
  'executive-leadership',
  'product-management',
  'programming',
] as const;

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  return {
    fallback: 'blocking',
    paths: legacySchoolSlugs.map((slug) => ({
      params: { slug },
      locale: DEFAULT_LANGUAGE,
    })),
  };
}
export async function getStaticProps({ params, locale }: GetStaticPropsContext<{ slug: string }>) {
  const schoolSlug = params?.slug;

  // Stop additional locale pages except the default locale from being built
  // Note: we are only building the legacy school pages for now using the old design.
  if (locale !== DEFAULT_LANGUAGE || !schoolSlug || !legacySchoolSlugs.includes(schoolSlug)) {
    return {
      notFound: true,
    };
  }

  if (!schoolSlug) {
    logger.fatal('School slug not found', {
      file: 'src/pages/school/[slug].tsx',
      function: 'getStaticProps',
      schoolSlug,
    });

    throw new Error('School slug not found');
  }

  const [page, schoolContent, otherSchools] = await Promise.all([
    getPage('/school'),
    getSchoolContent(schoolSlug),
    getAllSchoolContent({ omittedSchoolSlugs: [schoolSlug] }),
  ]);

  if (!page || !schoolContent) {
    return {
      notFound: true,
    };
  }

  if (schoolContent.seo) {
    page.seo = schoolContent.seo;
  }

  // Note: make sure we only fetch what we need. There may be duplicate program keys when flat mapping.
  const allLearningPathProgramKeys = new Set(
    schoolContent?.learningPaths
      ?.flatMap((path) => path.programs)
      .map((program) => program?.key)
      .filter(Boolean) || []
  );

  const learningPathProgramContentMap = await getLearningPathsProgramContentMap([...allLearningPathProgramKeys]);
  const catalogItems = getCatalogItemsFromServer(schoolContent?.programs?.map((program) => program.key) || []);

  const [freeCatalogItems, topCatalogItems] = partition(catalogItems, (item) => item.isFree);

  let downloadLearningPathsProps: ReturnType<typeof getLearningPathsProps> | null = null;

  if (publicEnv.NEXT_PUBLIC_ENVIRONMENT_NAME === 'test') {
    const testLearningPathsProps = await import('~/features/learning-pathways/data.test.json').then(
      (res) => res.default
    );
    downloadLearningPathsProps = getLearningPathsProps(schoolSlug, testLearningPathsProps);
  } else {
    downloadLearningPathsProps = getLearningPathsProps(schoolSlug, learningPathsProps);
  }

  // TODO: remove once we completely full scale the new school lp designs.
  // Removing this field to reduce JSON size.
  schoolContent.footerBanner = null;
  schoolContent.description2 = null;
  schoolContent.enterpriseSection = null;
  schoolContent.externalDescription = null;
  schoolContent.subcategories = null;
  schoolContent.featuredProgramsListTitle = null;
  schoolContent.graphic2 = null;
  schoolContent.featuredPrograms = null;

  return {
    props: {
      page,
      schoolContent,
      otherSchools: otherSchools.map(({ title, slug }) => ({ title, href: `/school/${slug}` })),
      learningPathProgramContentMap,
      freeCatalogItems,
      topCatalogItems,
      downloadLearningPathsProps,
    },
  };
}

function getLearningPathsProps(schoolSlug: string, props: typeof learningPathsProps) {
  switch (schoolSlug) {
    case 'data-science':
    case 'artificial-intelligence':
      return props.engineering;
    case 'product-management':
      return props.product_management;
    default:
      return null;
  }
}
